import React from 'react';
import Image from 'next/image';
import imageSizes from '@/data/imageSizes.json';

/**
 * Justified photo row — the site-wide rule for photos whose shape doesn't match their slot:
 * never a blurred fill and never a hard crop. Instead, related photos are placed side by side,
 * each taking width in proportion to its real shape, so they share one height exactly.
 *
 * Real image sizes come from src/data/imageSizes.json (regenerated whenever photos are added).
 */

const SIZES = imageSizes as unknown as Record<string, [number, number]>;

export interface RowPhoto {
  src: string;
  alt: string;
  /** width / height; looked up from imageSizes.json when omitted */
  ratio?: number;
}

export function ratioOf(p: RowPhoto, fallback = 4 / 3): number {
  if (p.ratio) return p.ratio;
  const s = SIZES[p.src];
  return s ? s[0] / s[1] : fallback;
}

/**
 * Picks the first photo plus as many of the following ones (up to `max`) as bring the
 * combined width/height closest to `target`.
 */
export function pickRow<T extends RowPhoto>(photos: T[], target: number, max = 3): T[] {
  const list = photos.filter((p, i) => p.src && photos.findIndex((q) => q.src === p.src) === i);
  if (!list.length) return [];
  let best = [list[0]];
  let sum = ratioOf(list[0]);
  let bestDiff = Math.abs(sum - target);
  for (let k = 1; k < Math.min(max, list.length); k++) {
    sum += ratioOf(list[k]);
    const diff = Math.abs(sum - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = list.slice(0, k + 1);
    }
  }
  return best;
}

export interface PhotoRowItem extends RowPhoto {
  overlay?: React.ReactNode;
  caption?: React.ReactNode;
}

export default function PhotoRow({
  items,
  gap = 8,
  stackOnMobile = false,
  priority = false,
  sizes = '(min-width: 1024px) 50vw, 100vw',
  className = '',
}: {
  items: PhotoRowItem[];
  gap?: number;
  /** Phones: one photo per line instead of a squeezed row */
  stackOnMobile?: boolean;
  priority?: boolean;
  sizes?: string;
  className?: string;
}) {
  return (
    <div className={`flex ${stackOnMobile ? 'flex-col sm:flex-row' : 'flex-row'} w-full ${className}`} style={{ gap }}>
      {items.map((it, i) => {
        const r = ratioOf(it);
        return (
          <figure
            key={it.src + i}
            className={stackOnMobile ? 'w-full sm:w-auto sm:[flex:var(--r)_1_0%] m-0' : 'm-0'}
            // ×100: flex-grow values summing below 1 only hand out that fraction of the space
            style={(stackOnMobile ? { ['--r' as string]: r * 100 } : { flex: `${r * 100} 1 0%` }) as React.CSSProperties}
          >
            <div className="relative w-full overflow-hidden" style={{ aspectRatio: String(r) }}>
              <Image src={it.src} alt={it.alt} fill priority={priority && i === 0} sizes={sizes} className="object-cover" />
              {it.overlay}
            </div>
            {it.caption}
          </figure>
        );
      })}
    </div>
  );
}
